import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Check, Mail, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import http, { formatError } from "@/lib/api";

const TABS = [
    { k: "artworks", label: "Œuvres" },
    { k: "exhibitions", label: "Expositions" },
    { k: "testimonials", label: "Livre d'or" },
    { k: "contacts", label: "Messages" },
];

const CATS = ["portraits", "asiatique", "paysages", "nu"];

const EMPTY_ARTWORK = { title: "", category: "portraits", description: "", medium: "", year: "", image_url: "", video_url: "", featured: false };
const EMPTY_EXH = { title: "", location: "", year: new Date().getFullYear(), month: "", description: "", video_url: "", image_url: "" };

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("artworks");

    const [artworks, setArtworks] = useState([]);
    const [exhibitions, setExhibitions] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [contacts, setContacts] = useState([]);

    const [editArtwork, setEditArtwork] = useState(null);
    const [editExh, setEditExh] = useState(null);
    const [editTesti, setEditTesti] = useState(null);

    const loadAll = async () => {
        try {
            const [a, e, t, c] = await Promise.all([
                http.get("/artworks"),
                http.get("/exhibitions"),
                http.get("/admin/testimonials"),
                http.get("/admin/contacts"),
            ]);
            setArtworks(a.data);
            setExhibitions(e.data);
            setTestimonials(t.data);
            setContacts(c.data);
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    useEffect(() => {
        if (user) loadAll();
    }, [user]);

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;

    const doLogout = async () => {
        await logout();
        nav("/admin/login", { replace: true });
    };

    // Save artwork
    const saveArtwork = async (a) => {
        try {
            const payload = { ...a, year: a.year ? Number(a.year) : null };
            if (a.id) await http.put(`/artworks/${a.id}`, payload);
            else await http.post("/artworks", payload);
            toast.success("Œuvre enregistrée");
            setEditArtwork(null);
            loadAll();
        } catch (err) { toast.error(formatError(err)); }
    };

    const deleteArtwork = async (id) => {
        if (!window.confirm("Supprimer cette œuvre ?")) return;
        try { await http.delete(`/artworks/${id}`); toast.success("Supprimée"); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    const saveExh = async (e) => {
        try {
            const payload = { ...e, year: Number(e.year) };
            if (e.id) await http.put(`/exhibitions/${e.id}`, payload);
            else await http.post("/exhibitions", payload);
            toast.success("Exposition enregistrée");
            setEditExh(null);
            loadAll();
        } catch (err) { toast.error(formatError(err)); }
    };

    const deleteExh = async (id) => {
        if (!window.confirm("Supprimer cette exposition ?")) return;
        try { await http.delete(`/exhibitions/${id}`); toast.success("Supprimée"); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    const approveTesti = async (id) => {
        try { await http.put(`/admin/testimonials/${id}/approve`); toast.success("Publié"); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    const deleteTesti = async (id) => {
        if (!window.confirm("Supprimer ce témoignage ?")) return;
        try { await http.delete(`/admin/testimonials/${id}`); toast.success("Supprimé"); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    const unapproveTesti = async (id) => {
        try { await http.put(`/admin/testimonials/${id}/unapprove`); toast.success("Masqué"); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    const saveTesti = async (t) => {
        try {
            if (t.id) await http.put(`/admin/testimonials/${t.id}`, { name: t.name, message: t.message, location: t.location });
            else await http.post("/admin/testimonials", { name: t.name, message: t.message, location: t.location });
            toast.success("Témoignage enregistré");
            setEditTesti(null);
            loadAll();
        } catch (err) { toast.error(formatError(err)); }
    };

    const markReadContact = async (id) => {
        try { await http.put(`/admin/contacts/${id}/read`); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };
    const deleteContact = async (id) => {
        if (!window.confirm("Supprimer ce message ?")) return;
        try { await http.delete(`/admin/contacts/${id}`); loadAll(); }
        catch (err) { toast.error(formatError(err)); }
    };

    return (
        <main data-testid="admin-dashboard-page" className="min-h-screen bg-ink-950 pt-28 pb-24">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                    <div>
                        <div className="overline mb-2">Atelier · Console</div>
                        <h1 className="serif text-4xl">Bonjour, {user.name}</h1>
                    </div>
                    <button data-testid="admin-logout" onClick={doLogout} className="btn-ghost">
                        <LogOut size={14} /> Déconnexion
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap items-center gap-2 border-b border-white/10 mb-10">
                    {TABS.map((t) => (
                        <button
                            key={t.k}
                            data-testid={`admin-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === t.k ? "text-gilt border-b border-gilt -mb-px" : "text-bone-300 hover:text-bone-50"
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ARTWORKS */}
                {tab === "artworks" && (
                    <section>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="serif text-2xl">{artworks.length} œuvres</h2>
                            <button onClick={() => setEditArtwork({ ...EMPTY_ARTWORK })} className="btn-gilt" data-testid="add-artwork">
                                <Plus size={14} /> Ajouter
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {artworks.map((a) => (
                                <div key={a.id} className="border border-white/10 bg-ink-850/50 p-4">
                                    <div className="aspect-[4/3] bg-ink-800 overflow-hidden mb-3">
                                        {a.image_url && <img src={a.image_url} alt={a.title} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex justify-between items-start gap-2 mb-1">
                                        <h3 className="serif text-xl">{a.title}</h3>
                                        {a.featured && <span className="text-[9px] uppercase tracking-widest text-gilt border border-gilt px-2 py-0.5">★</span>}
                                    </div>
                                    <div className="overline mb-3 text-bone-400">{a.category} {a.year ? `· ${a.year}` : ""}</div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditArtwork(a)} className="btn-ghost !text-[10px] !p-2"><Pencil size={12} /></button>
                                        <button onClick={() => deleteArtwork(a.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-red-400"><Trash2 size={12} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {editArtwork && (
                            <ArtworkModal
                                value={editArtwork}
                                onChange={setEditArtwork}
                                onSave={() => saveArtwork(editArtwork)}
                                onClose={() => setEditArtwork(null)}
                            />
                        )}
                    </section>
                )}

                {/* EXHIBITIONS */}
                {tab === "exhibitions" && (
                    <section>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="serif text-2xl">{exhibitions.length} expositions</h2>
                            <button onClick={() => setEditExh({ ...EMPTY_EXH })} className="btn-gilt" data-testid="add-exhibition">
                                <Plus size={14} /> Ajouter
                            </button>
                        </div>
                        <div className="space-y-4">
                            {exhibitions.map((ex) => (
                                <div key={ex.id} className="border border-white/10 bg-ink-850/50 p-5 flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="overline text-gilt mb-1">{ex.month ? `${ex.month} ` : ""}{ex.year}</div>
                                        <h3 className="serif text-xl">{ex.title}</h3>
                                        <p className="text-sm text-bone-400">{ex.location}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditExh(ex)} className="btn-ghost !text-[10px] !p-2"><Pencil size={12} /></button>
                                        <button onClick={() => deleteExh(ex.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-red-400"><Trash2 size={12} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {editExh && (
                            <ExhibitionModal
                                value={editExh}
                                onChange={setEditExh}
                                onSave={() => saveExh(editExh)}
                                onClose={() => setEditExh(null)}
                            />
                        )}
                    </section>
                )}

                {/* TESTIMONIALS */}
                {tab === "testimonials" && (
                    <section className="space-y-4">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="serif text-2xl">{testimonials.length} messages</h2>
                            <button onClick={() => setEditTesti({ name: "", message: "", location: "" })} className="btn-gilt" data-testid="add-testimonial">
                                <Plus size={14} /> Ajouter
                            </button>
                        </div>
                        {testimonials.map((t) => (
                            <div key={t.id} className="border border-white/10 bg-ink-850/50 p-6">
                                <div className="flex justify-between items-start mb-3 gap-4">
                                    <div>
                                        <div className="serif text-lg">{t.name} {t.location && <span className="text-bone-400 text-sm">· {t.location}</span>}</div>
                                        <div className={`text-[10px] uppercase tracking-widest mt-1 ${t.approved ? "text-gilt" : "text-terracotta"}`}>
                                            {t.approved ? "Publié" : "En attente"}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {t.approved ? (
                                            <button onClick={() => unapproveTesti(t.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-terracotta" title="Masquer du livre d'or">
                                                Masquer
                                            </button>
                                        ) : (
                                            <button onClick={() => approveTesti(t.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-gilt" data-testid={`approve-${t.id}`}>
                                                <Check size={12} /> Publier
                                            </button>
                                        )}
                                        <button onClick={() => setEditTesti(t)} className="btn-ghost !text-[10px] !p-2"><Pencil size={12} /></button>
                                        <button onClick={() => deleteTesti(t.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-red-400"><Trash2 size={12} /></button>
                                    </div>
                                </div>
                                <p className="italic-serif text-lg text-bone-100">« {t.message} »</p>
                            </div>
                        ))}
                        {editTesti && (
                            <TestimonialModal
                                value={editTesti}
                                onChange={setEditTesti}
                                onSave={() => saveTesti(editTesti)}
                                onClose={() => setEditTesti(null)}
                            />
                        )}
                    </section>
                )}

                {/* CONTACTS */}
                {tab === "contacts" && (
                    <section className="space-y-4">
                        <h2 className="serif text-2xl mb-6">{contacts.length} messages</h2>
                        {contacts.map((c) => (
                            <div key={c.id} className={`border p-6 ${c.read ? "border-white/5 bg-ink-900/30" : "border-gilt/40 bg-ink-850/50"}`}>
                                <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                                    <div>
                                        <div className="serif text-lg flex items-center gap-3">
                                            {!c.read && <span className="w-2 h-2 bg-gilt rounded-full" />}
                                            {c.name}
                                        </div>
                                        <a href={`mailto:${c.email}`} className="text-sm text-bone-300 hover:text-gilt flex items-center gap-2">
                                            <Mail size={12} /> {c.email} {c.phone && `· ${c.phone}`}
                                        </a>
                                        <div className="overline text-bone-400 mt-1">{c.subject.replace("_", " ")}</div>
                                    </div>
                                    <div className="flex gap-2">
                                        {!c.read && (
                                            <button onClick={() => markReadContact(c.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-gilt">
                                                <Check size={12} /> Lu
                                            </button>
                                        )}
                                        <button onClick={() => deleteContact(c.id)} className="btn-ghost !text-[10px] !p-2 hover:!text-red-400">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-bone-200 whitespace-pre-wrap leading-relaxed">{c.message}</p>
                                <div className="text-[10px] text-bone-500 mt-3">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </main>
    );
}

function ArtworkModal({ value, onChange, onSave, onClose }) {
    return (
        <Modal onClose={onClose}>
            <h3 className="serif text-3xl mb-6">{value.id ? "Éditer" : "Nouvelle"} œuvre</h3>
            <div className="space-y-5">
                <Input v={value.title} onCh={(x) => onChange({ ...value, title: x })} placeholder="Titre" testid="art-title" />
                <select className="input-line bg-ink-900" value={value.category} onChange={(e) => onChange({ ...value, category: e.target.value })} data-testid="art-category">
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input v={value.medium} onCh={(x) => onChange({ ...value, medium: x })} placeholder="Technique (huile, acrylique…)" testid="art-medium" />
                <Input v={value.year} onCh={(x) => onChange({ ...value, year: x })} placeholder="Année" type="number" testid="art-year" />
                <Input v={value.image_url} onCh={(x) => onChange({ ...value, image_url: x })} placeholder="URL image" testid="art-image" />
                <Input v={value.video_url} onCh={(x) => onChange({ ...value, video_url: x })} placeholder="URL vidéo YouTube (optionnel)" testid="art-video" />
                <textarea className="input-line resize-none" rows={4} value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} placeholder="Description" data-testid="art-description" />
                <label className="flex items-center gap-3 text-sm text-bone-200">
                    <input type="checkbox" checked={!!value.featured} onChange={(e) => onChange({ ...value, featured: e.target.checked })} data-testid="art-featured" />
                    Mettre en avant sur l'accueil
                </label>
            </div>
            <div className="flex gap-3 mt-8 justify-end">
                <button onClick={onClose} className="btn-ghost">Annuler</button>
                <button onClick={onSave} className="btn-gilt" data-testid="art-save">Enregistrer</button>
            </div>
        </Modal>
    );
}

function ExhibitionModal({ value, onChange, onSave, onClose }) {
    return (
        <Modal onClose={onClose}>
            <h3 className="serif text-3xl mb-6">{value.id ? "Éditer" : "Nouvelle"} exposition</h3>
            <div className="space-y-5">
                <Input v={value.title} onCh={(x) => onChange({ ...value, title: x })} placeholder="Titre" testid="ex-title" />
                <Input v={value.location} onCh={(x) => onChange({ ...value, location: x })} placeholder="Lieu" testid="ex-location" />
                <Input v={value.year} onCh={(x) => onChange({ ...value, year: x })} placeholder="Année" type="number" testid="ex-year" />
                <Input v={value.month} onCh={(x) => onChange({ ...value, month: x })} placeholder="Mois" testid="ex-month" />
                <Input v={value.image_url} onCh={(x) => onChange({ ...value, image_url: x })} placeholder="URL image" testid="ex-image" />
                <Input v={value.video_url} onCh={(x) => onChange({ ...value, video_url: x })} placeholder="URL vidéo YouTube" testid="ex-video" />
                <textarea className="input-line resize-none" rows={4} value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} placeholder="Description" data-testid="ex-description" />
            </div>
            <div className="flex gap-3 mt-8 justify-end">
                <button onClick={onClose} className="btn-ghost">Annuler</button>
                <button onClick={onSave} className="btn-gilt" data-testid="ex-save">Enregistrer</button>
            </div>
        </Modal>
    );
}

function TestimonialModal({ value, onChange, onSave, onClose }) {
    return (
        <Modal onClose={onClose}>
            <h3 className="serif text-3xl mb-6">{value.id ? "Éditer" : "Nouveau"} témoignage</h3>
            <div className="space-y-5">
                <Input v={value.name} onCh={(x) => onChange({ ...value, name: x })} placeholder="Nom" testid="testi-name" />
                <Input v={value.location} onCh={(x) => onChange({ ...value, location: x })} placeholder="Lieu (optionnel)" testid="testi-location" />
                <textarea className="input-line resize-none" rows={5} value={value.message} onChange={(e) => onChange({ ...value, message: e.target.value })} placeholder="Message" data-testid="testi-message" />
            </div>
            <div className="flex gap-3 mt-8 justify-end">
                <button onClick={onClose} className="btn-ghost">Annuler</button>
                <button onClick={onSave} className="btn-gilt" data-testid="testi-save">Enregistrer</button>
            </div>
        </Modal>
    );
}

function Modal({ children, onClose }) {
    return (
        <div
            className="fixed inset-0 z-[100] backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto"
            style={{ backgroundColor: "var(--bg)" }}
            onClick={onClose}
        >
            <div className="bg-[var(--bg-elev)] border border-[var(--border)] p-8 md:p-10 max-w-2xl w-full my-12 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-bone-300 hover:text-gilt"><X size={20} /></button>
                {children}
            </div>
        </div>
    );
}

function Input({ v, onCh, placeholder, type = "text", testid }) {
    return (
        <input type={type} className="input-line" value={v ?? ""} onChange={(e) => onCh(e.target.value)} placeholder={placeholder} data-testid={testid} />
    );
}
